import { f7ready } from "framework7-react";
import { onMessageListener, requestFirebaseNotificationPermission } from "../firebase/firebase";
import { loginServices } from "services/login/login.services";
import { UAParser } from "ua-parser-js";
import { v4 as uuidv4 } from 'uuid';
import { store } from "../store/store";
import { passesActions } from "actions/passes/passes.actions";
import { getTokenFromStorage } from "./authentication";
import { isIOS, isAndroid } from "react-device-detect";

// get user details from storage
export const getUserDetails = () => {
    const userDetails = localStorage.getItem("userDetails") !== null ? JSON.parse(localStorage.getItem("userDetails") || "") : null;
    if (userDetails) {
      return userDetails;
    } else {
      return "";
    }
  }

// check if it is mobile device or not (true only for iOS and Android, including tablets/iPads)
export const isMobileDevice = () => {
  return isIOS || isAndroid;
}

// Fires the daily user-active ping if the stored date differs from today.
export const checkUserActivityFromStorage = () => {
  if (!getTokenFromStorage()) return;
  const today = new Date().toDateString();
  const last = localStorage.getItem("lastActivity");
  if (last === today) return;
  localStorage.setItem("lastActivity", today);
  (store.dispatch as any)(passesActions.checkUserActive());
}

// get location from storage
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject("Geolocation not supported");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };

        resolve(location);
      },
      (error) => reject(error),
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10000
      }
    );
  });
};

// common function for set focus on header in login popup when popup open :: ADA :: 04-07-2023
export const setFocusOnHeaderInLoginPopup = (e) => {
  // remember the element that opened the popup so focus can return to it on close
  let triggerElement = (e && e.currentTarget) || document.activeElement;
  f7ready((f7) => {
    let popup = f7.popup.get('.loginModel');
    if (popup) {
      popup.open();
    }
    setTimeout(() => {
      let mainLoginPopupModal = document.querySelector("#popupLogin") as any;
      if (mainLoginPopupModal) {
        // store trigger element on the modal so handleClosePopup can restore focus to it
        mainLoginPopupModal.loginTriggerElement = triggerElement;
        let headerElement = mainLoginPopupModal.getElementsByClassName('cardHeader')[0]?.querySelector('.heading') as any;
        headerElement?.focus();
        // remove the container from the tab order so focus never lands on the whole modal
        mainLoginPopupModal.removeAttribute("tabIndex");
        // trap Tab / Shift + Tab focus within the login popup (forward + backward flow)
        mainLoginPopupModal.removeEventListener('keydown', trapLoginPopupFocus);
        mainLoginPopupModal.addEventListener('keydown', trapLoginPopupFocus);
      }
    }, 500)
    // if (e?.detail === 0) {
    //   setTimeout(() => {
    //     let mainLoginPopupModal = document.querySelector("#popupLogin") as any;
    //     if (mainLoginPopupModal) {
    //       let headerElement = mainLoginPopupModal.getElementsByClassName('cardHeader')[0].querySelector('.heading') as any;
    //       headerElement.focus();
    //       mainLoginPopupModal.setAttribute("tabIndex", "0");
    //       let elementArray = mainLoginPopupModal.getElementsByTagName("a") as any;
    //       if (elementArray) {
    //         for (let i = 0; i < elementArray.length; i++) {
    //           if (elementArray[i].classList.contains("external")) {
    //             elementArray[i].addEventListener('keydown', onKeyDownSetFocusFromLastToFirstElement);
    //             elementArray[i].firstElement = mainLoginPopupModal;
    //           }
    //         }
    //       }
    //     }
    //   }, 500)
    // }
    // else if (e?.detail === 1) {
    //   setTimeout(() => {
    //     let mainLoginPopupModal = document.querySelector("#popupLogin") as any;
    //     if (mainLoginPopupModal) {
    //       let textInputPhoneNumber = mainLoginPopupModal.querySelector("#txtPhonenumber") as any;
    //       if (textInputPhoneNumber) {
    //         textInputPhoneNumber.focus();
    //       }
    //     }
    //   }, 500)
    // }
    // else{
      
    // }
  });
} 

// trap Tab / Shift + Tab focus within the login popup, wrapping between the
// first and last interactive elements (so focus never lands on the whole modal)
const trapLoginPopupFocus = (e) => {
  if (e.keyCode !== 9) return;
  let activeSlide = e.currentTarget.querySelector(".open_login") || e.currentTarget;
  let focusableElements = Array.prototype.slice
    .call(activeSlide.querySelectorAll('a, button, input, [tabindex]:not([tabindex="-1"])'))
    .filter((element: any) => element.offsetParent !== null && !element.disabled);
  if (!focusableElements.length) return;
  let firstElement = focusableElements[0] as any;
  let lastElement = focusableElements[focusableElements.length - 1] as any;
  if (e.shiftKey && e.target === firstElement) {
    // backward flow: wrap from first element to last element
    e.preventDefault();
    lastElement.focus();
  } else if (!e.shiftKey && e.target === lastElement) {
    // forward flow: wrap from last element to first element
    e.preventDefault();
    firstElement.focus();
  }
}

// convert price value in us format "$1,234,567.89"
export const formatPrice = (number: any) => {
  let formatValue = number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  return formatValue.replace("$", ""); // replace $ sign to empty coz we already put that sign in page
}

export const formatSignedPrice = (number: any) => {
  const value = Number(number ?? 0);
  const safeValue = isNaN(value) ? 0 : value;
  return `${safeValue < 0 ? "-" : ""}$${formatPrice(Math.abs(safeValue))}`;
}



// get location permission
export const askLocationPermission = (): Promise<"granted" | "denied"> => {
  try {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve("denied");
        return;
      }
      navigator.geolocation.getCurrentPosition(
        () => resolve("granted"),
        (err) => {
          if (err.code === err.PERMISSION_DENIED) {
            resolve("denied");
          } else {
            resolve("denied");
          }
        }
      );
    });
  }
  catch (e) {
    console.log("error when ask location permission");
    return new Promise((resolve) => {
      resolve("denied");
      return;
    });
  }
};

// ask notification permission and get firebase token for push notification
export const askNotificationPermission = async () => {
  let isWebViewAvailable = (window as any).checkIsWebViewAvailable();
  let firebaseData = {} as any;
  if (isWebViewAvailable === true) {
    const tokenData = await (window as any).getDeviceInfoAndToken();
    if (tokenData) {
      console.log("tokenData>>>>>>>>>>>>", tokenData.uniqueId);
      firebaseData.deviceNotificationToken = tokenData.token;
      firebaseData.deviceUniqueId = tokenData.uniqueId;
      firebaseData.deviceMake = tokenData.make;
      firebaseData.deviceModel = tokenData.model;
      firebaseData.deviceOs = tokenData.device ? tokenData.device === "android" ? 2 : 1 : 2

      saveDeviceInfo(firebaseData);
    }
    localStorage.setItem("doNotAskNotificationPermission", "true");
  }
  else {
    requestFirebaseNotificationPermission().then((token) => {
      if (token) {
        console.log("FCM Token:", token);
        const parser = new UAParser();
        const result = parser.getResult();

        let storageUniqueId = localStorage.getItem("deviceUniqueId");
        if(!storageUniqueId){
          let guid = uuidv4();
          localStorage.setItem("deviceUniqueId", guid);
          storageUniqueId = guid;
        }
        
        firebaseData.deviceNotificationToken = token;
        firebaseData.deviceUniqueId = storageUniqueId;
        firebaseData.deviceMake = result ? result?.browser?.name : "";
        firebaseData.deviceModel = result ? result?.browser?.version : "";
        firebaseData.deviceOs = 3;

        saveDeviceInfo(firebaseData);
        // dispatch({ type: actionTypes.SET_FIREBASE_TOKEN, firebaseToken: token });
      }
    });

    // Foreground message listener
    const unsubscribe = onMessageListener((payload: any) => {
      console.log("Foreground message received:", payload);

      if (Notification.permission === "granted" && "serviceWorker" in navigator) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.showNotification(
            payload.data?.title ?? "New Message",
            {
              body: payload.data?.body ?? "",
              icon: "/Yodel192.png",
              tag: payload.data?.messageId,
              requireInteraction: true,
              data: {
                actionUrl: payload.data?.actionUrl ?? "https://portal-dev.yodelpass.com",
                messageId: payload.data?.messageId,
              },
            } as NotificationOptions
          );
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }
}

// save notification subscription token and device info 
const saveDeviceInfo = async (deviceInfo: any) => {
  let tempDeviceInfo = {
    deviceInfo: JSON.stringify(deviceInfo),
    userTooken: getTokenFromStorage() || null,
    isCalledFrom: "common file, after OTP - register-device",
    plateForm: "Portal",
  }
  console.log("deviceInfo>>>>>>>>>>>>", JSON.stringify(tempDeviceInfo));
  store.dispatch(passesActions.writeErroLogToServer(JSON.stringify(tempDeviceInfo)));
  let savedDeviceInfo = await loginServices.saveDeviceInfo(deviceInfo);
  localStorage.setItem("isNotificationPermissionAsked", "true");
}

// check notification permission is already asked or not for already user logged in
export const checkIsNotificationPermissionAsked = () => {
  let isNotificationPermissionAsked = localStorage.getItem("isNotificationPermissionAsked");
  if (isNotificationPermissionAsked === "true") {

  }
  else {
    askNotificationPermission();
    localStorage.setItem("isNotificationPermissionAsked", "true");
  }
}

// common function for open modal with focus trap - start :: ADA :: 08-06-2026

// walk up from modal to body, hiding every sibling at each level so
// VoiceOver/TalkBack cannot swipe outside the modal on mobile.
// tags each hidden element with the popupId so nested modals can restore independently
const hideBackgroundFromScreenReader = (popup: HTMLElement, popupId: string) => {
  let node: HTMLElement | null = popup;
  while (node && node !== document.body) {
    const parent = node.parentElement;
    if (parent) {
      Array.from<Element>(parent.children).forEach((sibling) => {
        if (sibling !== node) {
          if (!(sibling as any)._modalAriaHiddenBy) (sibling as any)._modalAriaHiddenBy = [];
          if (!(sibling as any)._modalAriaHiddenBy.includes(popupId)) (sibling as any)._modalAriaHiddenBy.push(popupId);
          sibling.setAttribute('aria-hidden', 'true');
        }
      });
    }
    node = parent;
  }
};

// restore only what this modal hid — leaves any parent modal's aria-hidden intact
const restoreBackgroundForModal = (popupId: string) => {
  document.querySelectorAll('[aria-hidden="true"]').forEach((el) => {
    const hiddenBy: string[] = (el as any)._modalAriaHiddenBy;
    if (!hiddenBy) return;
    const idx = hiddenBy.indexOf(popupId);
    if (idx !== -1) hiddenBy.splice(idx, 1);
    if (hiddenBy.length === 0) {
      el.removeAttribute('aria-hidden');
      delete (el as any)._modalAriaHiddenBy;
    }
  });
};

export const restoreBackgroundToScreenReader = () => {
  document.querySelectorAll('[aria-hidden="true"]').forEach((el) => {
    if ((el as any)._modalAriaHiddenBy) {
      el.removeAttribute('aria-hidden');
      delete (el as any)._modalAriaHiddenBy;
    }
  });
};

export const openModalWithFocusTrap = (popupId: string) => {
  setTimeout(() => {
    const popup = document.getElementById(popupId);
    if (!popup) return;
    // patch tabindex on F7 Button/Link elements that render as <a> without href
    popup.querySelectorAll<HTMLElement>('a:not([href])').forEach(el => {
      const cls = el.className || '';
      const isInteractive = cls.includes('button') || cls.includes('link') || cls.includes('themeBtn') || cls.includes('customBtn');
      if (isInteractive && !el.getAttribute('tabindex')) el.setAttribute('tabindex', '0');
    });
    // hide everything outside the modal from VoiceOver/TalkBack (mobile focus trap)
    hideBackgroundFromScreenReader(popup, popupId);
    const heading = popup.querySelector<HTMLElement>('.heading');
    heading?.focus({ preventScroll: true });
    attachFocusTrap(popupId);
  }, 150);
};

export const attachFocusTrap = (popupId: string) => {
  const popup = document.getElementById(popupId);
  if (!popup) return;

  const tabIndexObserver = new MutationObserver(() => {
    if (popup.hasAttribute('tabindex')) popup.removeAttribute('tabindex');
  });
  tabIndexObserver.observe(popup, { attributes: true, attributeFilter: ['tabindex'] });

  const getFocusable = (): HTMLElement[] =>
    Array.from(popup.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'
    )).filter(el => {
      if ((el as HTMLElement).getAttribute('tabindex') === '-1') return false;
      if (el.closest('[aria-hidden="true"]')) return false;
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });

  let lastShift = false;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    if (!popup.contains(document.activeElement)) return;
    lastShift = e.shiftKey;
    const focusable = getFocusable();
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement;
    const isFirst = active === first || first.contains(active);
    const isLast = active === last || last.contains(active);
    if (e.shiftKey && isFirst) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && isLast) {
      e.preventDefault();
      first.focus();
    }
  };

  // Overlays that open ON TOP of this modal (e.g. a smart-select country picker) render outside
  // this popup's DOM subtree. Focus legitimately moves into them, so the trap must NOT yank it back —
  // otherwise the cursor can't stay in the overlay's search/input fields.
  const isInOverlayOnTop = (el: HTMLElement | null) =>
    !!el?.closest('.smart-select-popup, .smart-select-sheet, .smart-select-popover, .sheet-modal, .popover, .picker, .dialog, .actions-modal');

  const handleFocusOut = (e: FocusEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget === popup) return;
    if (isInOverlayOnTop(relatedTarget)) return;
    if (!relatedTarget || !popup.contains(relatedTarget)) {
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      setTimeout(() => {
        // re-check after the focus settles: if it has moved into an overlay on top, leave it alone
        if (isInOverlayOnTop(document.activeElement as HTMLElement)) return;
        if (!popup.contains(document.activeElement)) {
          if (lastShift) focusable[focusable.length - 1].focus();
          else focusable[0].focus();
        }
      }, 0);
    }
  };

  (popup as any)._focusTrapHandler = handleKeyDown;
  (popup as any)._focusTrapFocusOut = handleFocusOut;
  (popup as any)._focusTrapObserver = tabIndexObserver;
  document.addEventListener('keydown', handleKeyDown, true);
  popup.addEventListener('focusout', handleFocusOut);
};

export const detachFocusTrap = (popupId: string) => {
  // restore only what this modal hid — leaves any parent modal's aria-hidden intact
  restoreBackgroundForModal(popupId);
  const popup = document.getElementById(popupId);
  if (popup) {
    if ((popup as any)._focusTrapHandler) {
      document.removeEventListener('keydown', (popup as any)._focusTrapHandler, true);
      delete (popup as any)._focusTrapHandler;
    }
    if ((popup as any)._focusTrapFocusOut) {
      popup.removeEventListener('focusout', (popup as any)._focusTrapFocusOut);
      delete (popup as any)._focusTrapFocusOut;
    }
    if ((popup as any)._focusTrapObserver) {
      (popup as any)._focusTrapObserver.disconnect();
      delete (popup as any)._focusTrapObserver;
    }
  }
};
// common function for open modal with focus trap - end :: ADA :: 08-06-2026

// ADA: normalize mixed-case names to Title Case so NVDA reads them
// as whole words instead of splitting at each capital letter. Display text is unchanged.
export const toReadableName = (name: any) =>
  name ? String(name).toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : "";

// append a cache-busting version token so a re-uploaded image served from the same url
// isn't shown from the browser cache.
export const getCacheBustedImageUrl = (imageUrl?: string | null) => {
  if (!imageUrl) return;
  return imageUrl + `?v=${new Date().getTime()}`;
}
// check if rating modal already open or not from flutter side
// returns true when the app rating banner should be shown
export const checkRatingModalPermission = async (): Promise<boolean> => {
  let enterprise = store.getState().global;
  const generalSetting = store.getState().passes?.generalSettingDetail?.data;
  // app rating must be enabled from general settings (EnableAppRating)
  if (generalSetting?.EnableAppRating?.toString().toLowerCase() !== "true") {
    return false;
  }
  if(enterprise?.baseName !== "/"){
    const notReallyTimestamp = localStorage.getItem("appRatingTimestamp");
    if (notReallyTimestamp) {
      const lastDate = parseInt(notReallyTimestamp, 10);
      // interval comes from general settings (AppRatingIntervalDays); fall back to 30 days if missing/invalid
      const intervalDays = parseInt(generalSetting?.AppRatingIntervalDays, 10);
      const ratingIntervalDays = !isNaN(intervalDays) && intervalDays > 0 ? intervalDays : 30;
      const intervalInMs = ratingIntervalDays * 24 * 60 * 60 * 1000;
      if (!isNaN(lastDate) && Date.now() - lastDate < intervalInMs) {
        return false;
      }
    }

    const isWebView = (window as any).checkIsWebViewAvailable();
    if (isWebView) {
      localStorage.setItem("appRatingTimestamp", Date.now().toString());
      return true;
    }
  }
  return false;
}

export const enableOtpAutofill = (
  numInputs: number,
  onCodeReceived: (code: string) => void,
  inputClassName: string = "otpFocusInput"
) => {
  let otpInputs = Array.prototype.slice.call(
    document.getElementsByClassName(inputClassName)
  ) as HTMLInputElement[];

  if (!otpInputs.length) {
    return () => { };
  }

  const handleAutofill = (e: any) => {
    let code = String(e.target.value || "").replace(/\D/g, "").slice(0, numInputs);
    if (code.length > 1) {
      e.target.value = "";
      onCodeReceived(code);
      if (code.length === numInputs) {
        e.target.blur();
      }
    }
  };

  otpInputs.forEach((inputEle: HTMLInputElement) => {
    inputEle.setAttribute("autocomplete", "one-time-code");
    inputEle.setAttribute("inputmode", "numeric");
    inputEle.setAttribute("maxlength", numInputs.toString());
    inputEle.addEventListener("input", handleAutofill);
  });

  // ios fills the code through the keyboard suggestion, but android chrome never reads the sms on
  // its own - a WebOTP request has to already be pending at the moment the sms lands. anything
  // that arrives while nothing is waiting is dropped and can never be read afterwards, which is
  // why every resend needs the request re-armed. also needs https and an sms body whose last
  // line binds the code to this origin, eg: "@portal.yodelpass.com #1234"
  let otpRequest: AbortController | null = null;
  let isDetached = false;
  // one request only ever yields one code, so re-arm after each hit to cover resends.
  // capped so a browser that keeps resolving instantly cannot spin here
  let armAttemptsLeft = 6;

  const armOtpRequest = () => {
    if (isDetached || armAttemptsLeft <= 0) {
      return;
    }
    armAttemptsLeft--;
    otpRequest = new AbortController();
    (navigator.credentials as any)
      .get({ otp: { transport: ["sms"] }, signal: otpRequest.signal })
      .then((credential: any) => {
        if (isDetached) {
          return;
        }
        let code = String(credential?.code || "").replace(/\D/g, "").slice(0, numInputs);
        if (code.length > 1) {
          onCodeReceived(code);
          if (code.length === numInputs) {
            otpInputs.forEach((inputEle: HTMLInputElement) => inputEle.blur());
          }
        }
        // stay armed for the next code in case this one is wrong or has already expired
        armOtpRequest();
      })
      .catch(() => {
        // aborted, prompt dismissed or unsupported in this webview. deliberately not re-armed -
        // those reject instantly and would loop. the user types the code instead
      });
  };

  if ("OTPCredential" in window && window.isSecureContext) {
    armOtpRequest();
  }

  return () => {
    isDetached = true;
    otpRequest?.abort();
    otpInputs.forEach((inputEle: HTMLInputElement) => {
      inputEle.removeEventListener("input", handleAutofill);
    });
  };
}

export const hideShowChatbot = (isModalOpen: boolean) => {
  let isNativeApp = (window as any).checkIsWebViewAvailable();
  if (!isNativeApp) {
    return;
  }
  let chatbotElement = document.getElementsByClassName("yodel-chat-widget")[0];
  if (chatbotElement) {
    if (isModalOpen) {
      chatbotElement.classList.add("display-none");
    }
    else {
      let isAlreadyModalOpen = document.getElementsByClassName("backdrop-in")?.length > 0;
      if(isAlreadyModalOpen){
        return;
      }
      chatbotElement.classList.remove("display-none");
    }
  }
}

export const printYodelIdCard = (onFinish?: () => void) => {
  const printClass = "printingYodelId";
  const cardClasses = ["yodelIdPdfCard", "corpYodelIdPdfCard", "walletYodelIdPdfCard"];
  document.body.classList.add(printClass);

  const hiddenElements = Array.from(document.body.children).filter(
    (child) => !cardClasses.some((cardClass) => child.classList.contains(cardClass))
  );
  hiddenElements.forEach((child) => child.setAttribute("data-yodel-print-hidden", "true"));

  let isFinished = false;
  let fallbackTimer: any;
  let listenTimer: any;

  const bodyClassObserver = new MutationObserver(() => {
    if (!document.body.classList.contains(printClass)) {
      document.body.classList.add(printClass);
    }
  });
  bodyClassObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });

  const finishPrint = () => {
    if (isFinished) {
      return;
    }
    isFinished = true;
    clearTimeout(listenTimer);
    clearTimeout(fallbackTimer);
    window.removeEventListener("pointerdown", finishPrint);
    window.removeEventListener("keydown", finishPrint);
    bodyClassObserver.disconnect();
    document.body.classList.remove(printClass);
    hiddenElements.forEach((child) => child.removeAttribute("data-yodel-print-hidden"));
    onFinish?.();
  };

  listenTimer = setTimeout(() => {
    window.addEventListener("pointerdown", finishPrint);
    window.addEventListener("keydown", finishPrint);
  }, 1000);
  // guarantee recovery even if the page never gets either signal back
  fallbackTimer = setTimeout(finishPrint, 60000);

  window.print();
}
